import { useEffect, useMemo, useRef, useState } from "react";
import axiosInstance from "@/lib/axios";
import { dashFollowUpNextSlot } from "@/config/endpoints";
import { Button } from "@/components/ui/button";
import { formatDateParts } from "@/utils/date";
import {
  ArrowRight,
  Calendar,
  ChevronDown,
  Mail,
  MessageCircle,
  Phone,
  Sparkles,
  Flag,
  UserRound,
  Layers,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/library/dropdown-menu";
import { openCompose } from "@/features/gmail/stores/gmailSlice";
import { useGmailAccount } from "@/features/gmail/api/useGmailAccount";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import DatePickerCalendar from "./components/DatePickerCalendar";
import DeadlineSetterPopover from "./components/DeadlineSetterPopover";
import CollectPaymentButton from "@/features/razorpay/components/CollectPaymentButton";
import { useV2FilterOptions } from "@/features/Dashboard/v2/api/useV2FilterOptions";
import ConfirmStageMoveDialog from "@/features/leads/components/view-lead/Modals/ConfirmStageMoveDialog";
import { useLeadForm } from "@/features/leads/contexts/FormLeadContext";
import { useChildForm } from "@/features/leads/contexts/ChildProvider";
import { useActiveDataMutation } from "@/features/leads/hook/useActiveDataMutation";
import {
  describeMutingDeal,
  readDealFollowUpMute,
} from "@/features/leads/utils/dealFollowUpMute";
import { useAppSelector, useAppDispatch } from "@/stores/hook";
import { setTriggerReview } from "@/features/videoProduction/stores/videoProductionFunctionalSlice";
import { setTriggerReview as setAcademyTriggerReview } from "@/features/academy/stores/academyFunctionalSlice";
import useLeadPipeline from "@/features/leads/hook/useLeadPipeline";
import { toast } from "sonner";
import useCurrentIndustry from "@/hooks/globalapis/useCurrentIndustry";
import { usePermissions } from "@/rbac/usePermissions";
import useRolesPipelineLite from "@/features/users/api/useRolesPipelineLite";
import {
  useShowMoveToNextStage,
  useFollowUpTimeEnabled,
} from "@/features/leads/api/useLeadFormConfig";
import { useStatusRbac } from "@/rbac/useStatusRbac";
import { cn } from "@/lib/utils";
import { buildTelHref, buildWhatsAppUrl } from "@/constants/countryCodes";
import { buildEntityRejectionResolver } from "@/features/leads/utils/entityRejection";
import {
  buildFollowUpDaysResolver,
  buildFollowUpMaxDaysResolver,
  followUpDefaultDate,
  followUpMaxDate,
} from "@/features/leads/utils/followUpMaxDays";
import { setPendingStageDraft } from "@/features/leads/stores/leadFunctionalSlice";
import type { DatePickerScheduleLabels } from "./components/DatePickerCalendar";
import { format as formatDate, startOfDay } from "date-fns";

function safeParseDate(value?: string) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function calendarDaysSince(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const start = new Date(iso);
  start.setHours(0, 0, 0, 0);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return Math.max(0, Math.floor((now.getTime() - start.getTime()) / 86400000));
}

export default function ActionForNextFollowup() {
  const {
    watch,
    register: leadRegister,
    setValue: setLeadValue,
    lead,
  } = useLeadForm();
  const {
    watch: watchChild,
    register: childRegister,
    setValue: setChildValue,
    currentChildId,
  } = useChildForm();
  const {
    addFollowUp,
    leadAddFollowUp,
    childAddFollowUp,
    activeData,
    updateStage,
    isChildActive,
    deadlines,
    handleChildChange,
  } = useActiveDataMutation();

  /** Footer + popover read/write the deal form when a child is open, else the lead form. */
  const isDealContext = Boolean(isChildActive && currentChildId);
  const setValue = (name: "followUpDate", value: string, opts?: object) =>
    isDealContext
      ? setChildValue(name, value, opts as any)
      : setLeadValue(name, value, opts as any);

  const leadFollowUps = watch("followUps");
  const childFollowUps = watchChild("followUps");
  const { canStage, canFollowup, canDeadline, bypassesRbacChecks } =
    usePermissions();
  const { isStatusDeniedByRole } = useStatusRbac();
  const [isMoveConfirmOpen, setIsMoveConfirmOpen] = useState(false);
  const {
    currentUser: { user },
  } = useAppSelector((state) => state.auth);
  const { currentTab } = useAppSelector((state) => state.leadFunctional);
  const { isServiceDetails, pendingDealCount } = useAppSelector(
    (state) => state.videoProductionFunctional
  );
  const { isProgramDetails, pendingDealCount: academyPendingDealCount } =
    useAppSelector((state) => state.academyFunctional);
  const { industry } = useCurrentIndustry();
  const dispatch = useAppDispatch();
  const { pipeline } = useLeadPipeline();
  const { account: gmailAccount } = useGmailAccount();
  const leadEmail = (activeData as any)?.email as string | undefined;
  const canQuickSend = Boolean(gmailAccount && leadEmail);

  const isVideoServiceRoute =
    industry?.id === 4 && currentTab === "Service" && isServiceDetails;
  const isAcademyProgramRoute =
    industry?.id === 5 && currentTab === "Program" && isProgramDetails;
  const isDealReviewRoute = isVideoServiceRoute || isAcademyProgramRoute;
  const activePendingDealCount = isVideoServiceRoute
    ? pendingDealCount
    : academyPendingDealCount;

  const currentStage = activeData?.stage;

  /**
   * REJECTED ENTITY = NO FOLLOW-UP, FOR ANYONE (user directive 2026-09-01):
   * an entity in a rejection status/sub-status needs no follow-up — the
   * server already gates it out of every bucket, count and reminder
   * (canonical rule 5), so a chip or scheduler here would show a date no
   * surface counts. Hidden outright, admins included; it returns the moment
   * the entity is un-rejected. Two-level check via the shared resolver
   * (status OR sub-status, payload flag or pipeline lookup).
   */
  const isEntityRejected = useMemo(
    () => buildEntityRejectionResolver(pipeline),
    [pipeline],
  );
  const activeEntityRejected = isEntityRejected(activeData);

  /**
   * MAXIMUM FOLLOW-UP HORIZON (stage editor "Maximum days allowed", 2026-09-09):
   * the entity's sub-status → status → stage cap, resolved like the server's
   * `followup-policy.ts`. The calendar greys out later days and says why; the
   * server still rejects a later date, so a stale pipeline cache only costs
   * the user one clear error, never a silent write.
   */
  const resolveFollowUpMax = useMemo(
    () => buildFollowUpMaxDaysResolver(pipeline),
    [pipeline],
  );

  /**
   * THE UNSAVED STAGE/STATUS PICK (user directive 2026-09-23: "if i changed in
   * frontend the status, the follow-up's default timeline should be changed
   * via state… if status going to change, add Update status and followup
   * button… if no status update just create followUp thats it").
   *
   * While the drawer's Stage section holds a draft for THIS row, the follow-up
   * picker answers for the bucket the lead is about to be in, not the one it
   * is leaving: the dial's default date, the "Maximum days allowed" horizon
   * and the submit button all switch over, and creating the follow-up applies
   * the move afterwards. `entityKey` keeps a draft picked on another lead or
   * deal out of this one — the store outlives a row switch by a render.
   */
  const pendingStageDraft = useAppSelector(
    (state) => state.leadFunctional.pendingStageDraft,
  );
  const followUpEntityKey = `${isDealContext ? "child" : "lead"}:${
    activeData?.id ?? ""
  }`;
  const pendingBucket =
    pendingStageDraft && pendingStageDraft.entityKey === followUpEntityKey
      ? pendingStageDraft
      : null;
  /** The bucket the follow-up is really for: the draft if any, else today's. */
  const followUpBucket = pendingBucket
    ? {
        stageId: pendingBucket.stageId,
        statusId: pendingBucket.statusId,
        subStatusId: pendingBucket.subStatusId,
      }
    : activeData;

  const followUpCap = resolveFollowUpMax(followUpBucket);
  const followUpMaxDay = followUpMaxDate(followUpCap.maxDays);
  // Under a draft the cap belongs to a named status the user just picked, so
  // say which one — "this status" would point at the one they are leaving.
  const capWhere = pendingBucket
    ? pendingBucket.subStatusName && followUpCap.from === "sub-status"
      ? pendingBucket.subStatusName
      : pendingBucket.statusName || `this ${followUpCap.from}`
    : `this ${followUpCap.from}`;
  const followUpMaxHint =
    followUpCap.maxDays != null && followUpMaxDay
      ? followUpCap.maxDays === 0
        ? `Follow-ups in ${pendingBucket ? capWhere : `this ${followUpCap.from}`} can only be set for today.`
        : `Follow-ups in ${pendingBucket ? capWhere : `this ${followUpCap.from}`} can be set at most ${followUpCap.maxDays} day${followUpCap.maxDays === 1 ? "" : "s"} out — until ${formatDate(followUpMaxDay, "dd MMM yyyy")}.`
      : undefined;

  const nextStage = useMemo(() => {
    if (!pipeline?.length || !currentStage) return null;
    const sorted = [...pipeline].sort(
      (a: any, b: any) => Number(a.order) - Number(b.order)
    );

    // Never use `!currentStage.order` — stage order can be 0 (falsy) for the first stage.
    let currentOrder: number | undefined;
    if (currentStage.order != null && currentStage.order !== "") {
      const n = Number(currentStage.order);
      if (!Number.isNaN(n)) currentOrder = n;
    }
    if (currentOrder === undefined && currentStage.id != null) {
      const match = sorted.find((s: any) => s.id === currentStage.id);
      if (match != null && match.order != null) {
        const n = Number(match.order);
        if (!Number.isNaN(n)) currentOrder = n;
      }
    }
    if (currentOrder === undefined) {
      const idx = sorted.findIndex((s: any) => s.id === currentStage.id);
      if (idx === -1) return null;
      return sorted[idx + 1] ?? null;
    }

    return sorted.find((s: any) => Number(s.order) > currentOrder) ?? null;
  }, [pipeline, currentStage]);

  /**
   * `move` is a per-stage permission keyed on the SOURCE stage (matches
   * server-side StagePolicy.canMove). When the lead has no current stage
   * (e.g. mid-load), fall through to the existing nextStage-based disabled
   * state instead of hard-blocking on permissions.
   */
  const currentStageIdNum = useMemo(() => {
    const raw = currentStage?.id;
    if (raw == null || raw === "") return NaN;
    const n = Number(raw);
    return Number.isFinite(n) ? n : NaN;
  }, [currentStage]);

  const canMoveFromCurrentStage = useMemo(() => {
    if (bypassesRbacChecks) return true;
    if (Number.isNaN(currentStageIdNum)) return true;
    return canStage(currentStageIdNum, "stage_management", "move");
  }, [bypassesRbacChecks, canStage, currentStageIdNum]);

  /**
   * Org-wide provision (Settings → Manage → Lead View Actions). When off, the
   * one-click shortcut is hidden for EVERYONE, admins included — the point is
   * that the landing status gets picked deliberately in the Stage & Status
   * card rather than auto-selected. Not a permission: `canMoveFromCurrentStage`
   * above still decides who may move a lead at all. Defaults to `true` while
   * the config loads, so the button never flickers out of an open drawer.
   */
  const showMoveToNextStage = useShowMoveToNextStage();
  const followUpTimeEnabled = useFollowUpTimeEnabled();

  /**
   * Per-stage `lead_followup` / `lead_deadline` levels — view governs whether
   * we render the chip/pill at all; edit governs whether it's interactive.
   * View-only users still need to SEE the existing follow-up date and the
   * pending milestone; "Add" / popover affordances are gated on edit.
   * Both helpers return `true` for unknown stage so a transient loading
   * state never strobes the UI.
   */
  const canViewFollowup = canFollowup(currentStageIdNum, "view");
  const canEditFollowup = canFollowup(currentStageIdNum, "edit");
  const canViewDeadline = canDeadline(currentStageIdNum, "view");
  const canSetDeadline = canDeadline(currentStageIdNum, "edit");

  /**
   * ROLE-SLOT PICKER (role-based follow-ups, 2026-08-30). A follow-up belongs
   * to a (entity, role) slot. Personal-scope viewers always write their OWN
   * slot — the server resolves it, so they see no picker. Managers/admins
   * MUST pick whose follow-up they are setting — there is NO "Auto" ("if it
   * is a manager you gotta select a role"). Options are BOTH grains: the
   * active deal's assigned roles AND the lead's (the Counsellor usually
   * lives at LEAD grain — she must be pickable even while a deal is open,
   * and her follow-up is then written to the LEAD, where her slot lives).
   */
  type SlotOption = {
    grain: "lead" | "child";
    roleId: number;
    roleName: string;
    userName: string;
  };
  const [slotSel, setSlotSel] = useState<SlotOption | null>(null);
  const [slotSearch, setSlotSearch] = useState("");
  const {
    isManagerialViewer,
    isPersonalScopeViewer,
    followUpOwnStageIds,
    followUpRoleStages,
  } = useV2FilterOptions();
  // The viewer's own role OWNS the slot on this stage (it participates there
  // — the server's `actorOwnsSlot` write rule): their write can only land in
  // their own slot, and a picked foreign role would 403 ("You can only manage
  // your own follow-up on this lead"). So never ask — this is what makes a
  // personal-scoped user's follow-up INDIVIDUAL: no dropdown, own slot,
  // always (user directive 2026-08-31). Judged per-STAGE, not per-viewer, so
  // a mixed role (manages stage A, works stage B) is asked on A, never on B.
  const ownsSlotOnCurrentStage =
    !bypassesRbacChecks && followUpOwnStageIds.includes(currentStageIdNum);
  // `!isPersonalScopeViewer` (three-tier rule) closes the remaining gap: a
  // personal-scoped viewer whose role happens not to participate on THIS
  // stage (e.g. a reach-holder reading as managerial) must still never pick
  // whose follow-up they're setting.
  const pickerEligible =
    !ownsSlotOnCurrentStage &&
    Boolean(bypassesRbacChecks || (isManagerialViewer && !isPersonalScopeViewer));
  // Managers pick only from roles their hierarchy lets them SEE — the same
  // `forFilters:1` list the assign panels and filter dropdowns use (wizard
  // "exclude" toggles + roles ranked above the viewer drop out; the viewer's
  // own role never does). Admins exclude nothing, so they skip the fetch.
  // While the list is in flight nothing is filtered: exclusion is an
  // option-list tidy, not a permission.
  const { rolesPipeline: accessibleRoles } = useRolesPipelineLite(
    { forFilters: 1 },
    { enabled: pickerEligible && !bypassesRbacChecks },
  );
  const accessibleRoleIds = useMemo<Set<number> | null>(
    () =>
      !bypassesRbacChecks && Array.isArray(accessibleRoles)
        ? new Set(accessibleRoles.map((r: any) => Number(r.id)))
        : null,
    [bypassesRbacChecks, accessibleRoles],
  );
  // Detail payloads ship `leadUsers`/`childUsers` (transformed) — the raw
  // Prisma casings only appear on list rows. Read both.
  const dealRows: any[] = isDealContext
    ? ((activeData as any)?.childUsers ?? (activeData as any)?.ChildUsers ?? [])
    : [];
  const leadRows: any[] = isDealContext
    ? (((lead as any)?.leadUsers ?? (lead as any)?.LeadUsers ?? []) as any[])
    : ((activeData as any)?.leadUsers ?? (activeData as any)?.LeadUsers ?? []);
  const assignedSlots = useMemo<SlotOption[]>(() => {
    const out: SlotOption[] = [];
    const seen = new Set<string>();
    const collect = (rows: any[], grain: "lead" | "child") => {
      for (const row of rows) {
        const u = row?.user;
        const rid = u?.role?.id;
        if (!u?.id || rid == null || row?.isRevoked) continue;
        // Hierarchy-excluded / above-rank roles are invisible to this
        // manager everywhere else (assign panels, filter dropdowns) —
        // don't offer their slots here either.
        if (accessibleRoleIds && !accessibleRoleIds.has(Number(rid))) continue;
        const key = `${grain}:${rid}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push({
          grain,
          roleId: rid,
          roleName: u.role?.name ?? `Role ${rid}`,
          userName: u.name ?? "",
        });
      }
    };
    // Lead book first (the Counsellor), then the deal's roles — the same
    // order the drawer's Followups tab lists sections.
    collect(leadRows, "lead");
    collect(dealRows, "child");
    return out;
  }, [leadRows, dealRows, accessibleRoleIds]);
  // Only slots the server would ACCEPT: a write can only target a role that
  // participates on the entity's CURRENT stage (`participatesOnStage`, the
  // slot-validity rule that binds admins too). Offering an assignee whose
  // role has follow-ups off in this stage produced a 400 after the user had
  // picked a date and typed a note. Unfiltered while the map hasn't loaded.
  const slotOptions = useMemo<SlotOption[]>(() => {
    if (!followUpRoleStages || Number.isNaN(currentStageIdNum)) {
      return assignedSlots;
    }
    return assignedSlots.filter((o) =>
      (followUpRoleStages[String(o.roleId)] ?? []).includes(currentStageIdNum),
    );
  }, [assignedSlots, followUpRoleStages, currentStageIdNum]);
  const offStageSlots = useMemo(
    () => assignedSlots.filter((o) => !slotOptions.includes(o)),
    [assignedSlots, slotOptions],
  );
  const visibleSlotOptions = useMemo(() => {
    const q = slotSearch.trim().toLowerCase();
    if (!q) return slotOptions;
    return slotOptions.filter(
      (o) =>
        o.roleName.toLowerCase().includes(q) ||
        o.userName.toLowerCase().includes(q),
    );
  }, [slotOptions, slotSearch]);
  // Ask ONLY viewers who do NOT own the slot on this stage (admins, and
  // managerial viewers supervising here — `pickerEligible` above). A
  // personal-scope user is NEVER asked: they can only update their own slot,
  // the server resolves it (user directives 2026-08-30 / 08-31).
  const showSlotPicker =
    pickerEligible && slotOptions.length > 0 && canEditFollowup;
  const selectedSlot = slotSel;
  // One offerable slot → it IS the answer; don't make the user find a
  // dropdown to unlock Next. Drop a pick that is no longer offerable (deal
  // switched, roles reloaded) so a stale role never rides the write.
  useEffect(() => {
    if (!showSlotPicker) return;
    if (
      slotSel &&
      !slotOptions.some(
        (o) => o.grain === slotSel.grain && o.roleId === slotSel.roleId,
      )
    ) {
      setSlotSel(null);
      return;
    }
    if (!slotSel && slotOptions.length === 1) setSlotSel(slotOptions[0]);
  }, [showSlotPicker, slotOptions, slotSel]);
  // Nothing the server would accept: say WHY inside the modal (and hold
  // Next) instead of letting the write 400 as "Auto-save failed". Personal-
  // scope viewers never reach this — their own slot is server-resolved.
  const slotBlockedReason = useMemo<string | null>(() => {
    if (!pickerEligible || !canEditFollowup || slotOptions.length > 0) {
      return null;
    }
    const stageName = currentStage?.name ? String(currentStage.name) : "this stage";
    if (offStageSlots.length > 0) {
      const names = [...new Set(offStageSlots.map((o) => o.roleName))];
      const who = names.length === 1 ? names[0] : names.join(", ");
      return `${who} ${names.length === 1 ? "doesn't" : "don't"} do follow-ups in ${stageName}. Enable it under Roles → ${who} → ${stageName} → Follow-ups, or assign a user whose role does.`;
    }
    return "Assign a user first — a follow-up belongs to an assignee's role.";
  }, [pickerEligible, canEditFollowup, slotOptions.length, offStageSlots, currentStage?.name]);
  const slotBlockedNode = slotBlockedReason ? (
    <div className="flex items-start gap-2 px-4 py-2.5 border-b border-[#fde68a] bg-[#fffbeb]">
      <span className="mt-0.5 inline-flex size-4 shrink-0 items-center justify-center rounded-full bg-[#f59e0b] text-[10px] font-bold text-white">
        !
      </span>
      <span className="text-[11.5px] leading-[1.35] text-[#92400e]">
        {slotBlockedReason}
      </span>
    </div>
  ) : null;

  // Rendered INSIDE the date modal (DatePickerCalendar's `slotPicker` slot),
  // not beside the footer chip — "ask whom's follow-up you're updating on
  // the modal, not on that side".
  const slotPickerNode = showSlotPicker ? (
    <div className="flex items-center justify-between gap-2 px-4 py-2 border-b border-[#e6e8e7] bg-[#fbfcfc]">
      <span className="text-[10px] font-bold uppercase tracking-[0.06em] text-[#697571]">
        Whose follow-up
      </span>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={`inline-flex items-center gap-1.5 h-8 px-2.5 rounded-[10px] cursor-pointer transition-all bg-white border hover:bg-gray-50 ${
              selectedSlot
                ? "border-[var(--color-black-2)] hover:border-[var(--color-black-4)]"
                : "border-[#f59e0b] border-dashed"
            }`}
          >
            <UserRound
              className="size-3.5 text-[var(--color-black-8)]"
              strokeWidth={2.25}
            />
            <span
              className={`text-[12px] font-semibold tracking-[-0.2px] max-w-[130px] truncate ${
                selectedSlot
                  ? "text-[var(--color-text-primary)]"
                  : "text-[#b45309]"
              }`}
            >
              {selectedSlot ? selectedSlot.roleName : "Select role"}
            </span>
            <ChevronDown className="size-3 text-[var(--color-black-6)]" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[230px] z-[60] p-1">
          {/* Search — keys must stay in the input, not trigger the menu's
              typeahead. */}
          <div className="p-1 pb-1.5">
            <input
              autoFocus
              value={slotSearch}
              onChange={(e) => setSlotSearch(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              placeholder="Search role or person…"
              className="w-full h-8 px-2.5 rounded-[8px] border border-[var(--color-black-2)] text-[12px] outline-none focus:border-[var(--color-primary)] bg-white"
            />
          </div>
          {visibleSlotOptions.length === 0 && (
            <div className="px-2.5 py-2 text-[11px] text-[var(--color-text-gray)]">
              No matching role
            </div>
          )}
          {visibleSlotOptions.map((o) => (
            <DropdownMenuItem
              key={`${o.grain}:${o.roleId}`}
              onClick={() => setSlotSel(o)}
            >
              <span className="flex w-full items-center justify-between gap-2">
                <span className="flex flex-col">
                  <span className="text-[12px] font-medium">{o.roleName}</span>
                  <span className="text-[10.5px] text-[var(--color-text-gray)]">
                    {o.userName}
                  </span>
                </span>
                <span className="rounded border border-[var(--color-black-1)] px-1 py-px text-[9px] font-semibold uppercase tracking-wide text-[var(--color-black-6)]">
                  {o.grain === "lead" ? "Lead" : "Deal"}
                </span>
              </span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  ) : null;

  // Auto-pick the landing status for "move to next stage" — skip statuses
  // the role's "Status" block denies, falling back to the raw first so the
  // move is never silently dropped (the server still 403s a denied landing).
  const nextStageStatuses = nextStage?.statuses ?? [];
  const firstStatusOnNextStage =
    nextStageStatuses.find(
      (st: any) => !isStatusDeniedByRole(nextStage?.id, st.id)
    ) ?? nextStageStatuses[0];
  const firstSubStatusOnNextStage = firstStatusOnNextStage?.subStatuses?.[0];

  const handleConfirmMoveToNextStage = () => {
    if (!nextStage || !activeData?.id) return;
    if (!firstStatusOnNextStage) {
      toast.error("Next stage has no statuses configured");
      return;
    }
    updateStage.mutate(
      {
        id: activeData.id,
        data: {
          stageId: nextStage.id,
          statusId: firstStatusOnNextStage.id,
          ...(firstSubStatusOnNextStage
            ? { subStatusId: firstSubStatusOnNextStage.id }
            : {}),
        },
      },
      {
        onSuccess: () => {
          // Refresh lives in the updateStage hook (no duplicate GETs here).
          toast.success(`Moved to ${nextStage.name}`);
          setIsMoveConfirmOpen(false);
        },
        onError: (error: any) => {
          const msg =
            error.response?.data?.message || "Failed to move to next stage";
          toast.error(msg);
        },
      }
    );
  };

  const handleOpenMoveConfirm = () => {
    if (!nextStage || !activeData?.id) return;
    if (!canMoveFromCurrentStage) return;
    setIsMoveConfirmOpen(true);
  };

  /**
   * Single source of truth for the chip: the EARLIEST pending row in
   * `followUps` — i.e. the next-up the user needs to action. `followUpDate`
   * alone is stale for deals (always pointed at the lead form even when a
   * child is active, and addFollowUp didn't always sync the slot after
   * replace()).
   *
   * Why min-by-date instead of `Array.find`: the lead-detail endpoint
   * returns `followUpDates` ordered by `date: "desc"` (latest-first, see
   * `lead.repo.ts` getFullDetails). A naive `find(... status === "Pending")`
   * therefore picked the LAST pending entry (e.g. "25/05/26") even when an
   * earlier pending follow-up (the one driving the dashboard's "Today"
   * bucket) existed. That made the drawer and the dashboard disagree about
   * which date the lead was actually due on.
   */
  // PERSONAL scope reads its OWN slot only (user directive 2026-08-31,
  // decision 10: card chip = viewer's own slot; managers = earliest across
  // slots). Rows without a role (pre-upgrade history) don't count either —
  // the server lens hides those from every number this viewer sees, and "no
  // own pending row" correctly falls through to the virtual "today" below.
  const personalChipScope =
    !bypassesRbacChecks && isPersonalScopeViewer && user?.role?.id != null;
  const effectiveFollowUpDateIso = useMemo(() => {
    const base = isDealContext ? childFollowUps : leadFollowUps;
    // In a deal drawer the LEAD-grain slots still apply: a lead-assigned role
    // (the Counsellor) keeps her follow-up on the LEAD while a deal is open,
    // and the rail buckets the lead on that row. Union the lead list for
    // EVERY viewer — a personal viewer's own slot (filtered below) and a
    // manager's earliest-across-slots. Reading the deal list alone fell
    // through to "today" whenever the open deal had no rows of its own
    // (Skymark lead 343221, 2026-09-13: rail Sep 10, chip 13/09).
    const list = isDealContext
      ? [
          ...(Array.isArray(base) ? base : []),
          ...(Array.isArray(leadFollowUps) ? leadFollowUps : []),
        ]
      : base;
    const arr = Array.isArray(list) ? list : [];
    let earliest: { date: string; ts: number } | null = null;
    for (const f of arr) {
      if (!f || f.status !== "Pending" || !f.date) continue;
      if (personalChipScope) {
        const rid = (f as any)?.role?.id ?? (f as any)?.roleId;
        if (rid == null || Number(rid) !== Number(user.role.id)) continue;
      }
      const ts = new Date(f.date).getTime();
      if (!Number.isFinite(ts)) continue;
      if (earliest === null || ts < earliest.ts) {
        earliest = { date: f.date, ts };
      }
    }
    return earliest?.date ?? null;
  }, [
    isDealContext,
    childFollowUps,
    leadFollowUps,
    personalChipScope,
    user?.role?.id,
  ]);

  const formFollowUpDate = isDealContext
    ? watchChild("followUpDate")
    : watch("followUpDate");

  /**
   * Resolution order: earliest PENDING row → the form slot → **today**.
   *
   * The old third branch returned `null` when rows existed but none were
   * pending, which rendered a "Follow-ups complete" state. That concept is
   * gone (2026-08-20): a follow-up is a single always-live date that can only
   * be rescheduled, so "no pending row" now means "due today, nobody has
   * scheduled the next touch" — matching what the server counts in the
   * dashboard Today bucket and the leads-page follow-up filter
   * (`buildNoPendingFollowUpEntityWhere` / `LeadFilterBuilder.virtualTodayArm`).
   */
  const parsedDate = useMemo(() => {
    if (effectiveFollowUpDateIso) {
      return safeParseDate(effectiveFollowUpDateIso);
    }
    return safeParseDate(formFollowUpDate || undefined) ?? new Date();
  }, [effectiveFollowUpDateIso, formFollowUpDate]);

  const displayDate = parsedDate ?? new Date();
  const { day, month, year } = formatDateParts(displayDate);

  /**
   * DEFAULT FOLLOW-UP DATE UNDER A DRAFT — the target bucket's "Default
   * follow-up" dial (`followUpDays`), same resolution as the server's
   * lifecycle, so the picker opens on the day the move itself would have
   * scheduled. A bucket with nothing configured leaves the existing date
   * alone; a date beyond the new cap is pulled back to the last legal day so
   * the calendar never opens on a greyed-out square.
   */
  const resolveFollowUpDays = useMemo(
    () => buildFollowUpDaysResolver(pipeline),
    [pipeline],
  );
  /**
   * Kept as a TIMESTAMP, not a Date: `followUpMaxDate()` builds a fresh Date
   * on every render, and feeding that identity into the picker's `value`
   * effect would reseed its state on every render — a render loop. The
   * timestamp is stable for the whole day, so the Date below is too.
   */
  const followUpMaxTime = followUpMaxDay ? followUpMaxDay.getTime() : null;
  const displayTime = displayDate.getTime();
  const pickerTime = useMemo(() => {
    if (!pendingBucket) return displayTime;
    const { days } = resolveFollowUpDays({
      stageId: pendingBucket.stageId,
      statusId: pendingBucket.statusId,
      subStatusId: pendingBucket.subStatusId,
    });
    const dialDate = followUpDefaultDate(days);
    const seed = dialDate ? dialDate.getTime() : displayTime;
    if (followUpMaxTime != null && seed > followUpMaxTime) {
      // The cap is the END of its day; open the calendar on that day's start.
      return startOfDay(new Date(followUpMaxTime)).getTime();
    }
    return seed;
  }, [
    pendingBucket,
    displayTime,
    followUpMaxTime,
    resolveFollowUpDays,
  ]);
  const pickerValue = useMemo(
    () => (pickerTime === displayTime ? displayDate : new Date(pickerTime)),
    // `displayDate` is the memoised original — reused so the no-draft case
    // passes the exact same object it always did.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pickerTime, displayTime],
  );

  /**
   * Under a draft the picker's last step commits BOTH things, so it says so.
   * With no draft the copy is untouched (`undefined` → the component's own
   * defaults), which is the "if no status update just create followUp" case.
   */
  const followUpScheduleLabels: DatePickerScheduleLabels | undefined =
    pendingBucket
      ? {
          noteTitle: "Add follow-up note",
          notePlaceholder: "Type your follow-up note...",
          noteHint: `Note is optional. Creating this follow-up also ${
            pendingBucket.isStageMove ? "moves" : "updates"
          } this ${isDealContext ? "deal" : "lead"} to ${[
            pendingBucket.isStageMove ? pendingBucket.stageName : "",
            pendingBucket.statusName,
            pendingBucket.subStatusName,
          ]
            .filter(Boolean)
            .join(" · ")}.`,
          submit: pendingBucket.isStageMove
            ? "Update stage & follow-up"
            : "Update status & follow-up",
        }
      : undefined;

  /**
   * The SECOND way to update a status (the Update button in the Stage section
   * is the first). Run only after the follow-up row is really saved: the
   * server's "a follow-up is required" gate is then already satisfied, so the
   * move goes straight through instead of popping a second date prompt.
   * Everything else — requirements, notes, RBAC — is still judged server-side
   * exactly as it is for the Update button, and the draft survives a refusal
   * so the Stage section still shows what the user picked.
   */
  const applyPendingStageDraft = (draft: NonNullable<typeof pendingBucket>) => {
    const id = activeData?.id;
    if (!id) return;
    updateStage.mutate(
      {
        id,
        data: {
          stageId: draft.stageId,
          statusId: draft.statusId,
          subStatusId: draft.subStatusId,
        },
      },
      {
        onSuccess: () => {
          toast.success(
            draft.isStageMove
              ? `Follow-up saved · moved to ${draft.stageName} · ${draft.statusName}`
              : `Follow-up saved · status updated to ${draft.statusName}`,
          );
          dispatch(setPendingStageDraft(null));
        },
        onError: (error: any) => {
          toast.error(
            error?.response?.data?.message ||
              "Follow-up saved, but the status could not be updated.",
          );
        },
      },
    );
  };

  // RULE 9 ("Prioritise latest deal"): the open deal's follow-up is muted by
  // a newer sibling — the chip still shows the date (it is real and can be
  // rescheduled), with a pill saying no surface counts it.
  const dealFollowUpMute = isDealContext
    ? readDealFollowUpMute(activeData)
    : null;

  const handleDateChange = (date: Date | null) => {
    if (!date) {
      setValue("followUpDate", "", {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
      return;
    }

    setValue("followUpDate", date.toISOString(), {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  /**
   * AUTO-ADVANCE TO THE NEXT DUE DEAL (user directive 2026-08-31): a
   * personal-scope viewer who just cleared one deal's follow-up (rescheduled
   * it beyond today) often owes the SAME lead another touch on a different
   * deal — that lead stays in Today/Overdue until every one of their slots
   * is actioned. Ask the server which deal is next (the canonical bucket
   * rules, scoped to this one lead), announce it, and glide there.
   *
   * The check is delayed ~900ms: the follow-up PATCH is fire-and-forget, so
   * this gives it time to land and lets the server answer over the written
   * state (the just-written entity is excluded regardless, so a lost race
   * can only surface a deal that was already due for another reason).
   */
  const [nextDueDeal, setNextDueDeal] = useState<{
    childId: number;
    childName: string;
    remaining: number;
    overdue: boolean;
  } | null>(null);
  const nextDealTimers = useRef<number[]>([]);
  useEffect(
    () => () => {
      nextDealTimers.current.forEach((t) => window.clearTimeout(t));
    },
    [],
  );
  // Any deal switch — ours or a manual chip click — retires the banner.
  useEffect(() => {
    setNextDueDeal(null);
  }, [currentChildId]);
  // The glide: announce for ~2.8s (the banner's progress bar runs the same
  // clock), then switch the drawer to that deal. "Stay" cancels.
  useEffect(() => {
    if (!nextDueDeal) return;
    const t = window.setTimeout(() => {
      setNextDueDeal(null);
      handleChildChange(nextDueDeal.childId);
    }, 2800);
    nextDealTimers.current.push(t);
    return () => window.clearTimeout(t);
  }, [nextDueDeal, handleChildChange]);
  const maybeAdvanceToNextDueDeal = (
    newDate: Date,
    writtenChildId: number | null,
  ) => {
    // Personal viewers only — a manager picking whose follow-up to set is
    // not being walked through their own book.
    if (!isPersonalScopeViewer || bypassesRbacChecks) return;
    // Only when the write CLEARS today (IST, host-TZ safe): a follow-up set
    // for today keeps THIS deal due — jumping away would abandon it.
    const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
    const ist = new Date(Date.now() + IST_OFFSET_MS);
    const endOfTodayIstMs =
      Date.UTC(
        ist.getUTCFullYear(), ist.getUTCMonth(), ist.getUTCDate(),
        23, 59, 59, 999,
      ) - IST_OFFSET_MS;
    if (newDate.getTime() <= endOfTodayIstMs) return;
    const leadIdNum = Number(
      (lead as any)?.id ??
        (isDealContext ? (activeData as any)?.leadId : (activeData as any)?.id),
    );
    if (!Number.isFinite(leadIdNum) || leadIdNum <= 0) return;
    nextDealTimers.current.push(
      window.setTimeout(async () => {
        try {
          const res = await axiosInstance.get(dashFollowUpNextSlot, {
            params: {
              leadId: leadIdNum,
              ...(writtenChildId ? { excludeChildId: writtenChildId } : {}),
            },
          });
          const payload = res.data?.data;
          const slot = payload?.nextSlot;
          if (
            slot?.childId &&
            Number(slot.childId) !== Number(currentChildId)
          ) {
            setNextDueDeal({
              childId: Number(slot.childId),
              childName: slot.childName || "the next deal",
              remaining: Number(payload?.remainingDue) || 1,
              overdue: slot.bucket === "Overdue",
            });
          }
        } catch {
          // Advisory only — never let this interfere with the save flow.
        }
      }, 900),
    );
  };

  const handleCreateFollowUp = ({
    date,
    note,
  }: {
    date: Date;
    note: string;
  }) => {
    // A draft in the Stage section turns this into "Update status &
    // follow-up": the move runs only once the row is saved (see
    // applyPendingStageDraft). Captured now — the store is cleared on success
    // and the callback must still know what it applied.
    const draftToApply = pendingBucket;
    const followUpObj = {
      date: date.toISOString(),
      // The cap belongs to the bucket the follow-up is FOR. Without this the
      // server would judge the date against the status the user is LEAVING
      // (`resolveFollowUpCapBucket`), so a date the calendar just offered
      // under the target's dial could come back a 400. Same field the
      // "Follow-up required" transition popup sends.
      ...(draftToApply
        ? {
            transitionTarget: {
              stageId: draftToApply.stageId,
              statusId: draftToApply.statusId,
              subStatusId: draftToApply.subStatusId,
            },
          }
        : {}),
      // Whose SLOT this lands in. Managers/admins MUST have picked (no
      // Auto); personal-scope users send nothing and the server resolves
      // their own slot.
      ...(showSlotPicker && selectedSlot
        ? { roleId: selectedSlot.roleId }
        : {}),
      // Local display hint so the drawer timeline groups the new row into
      // the right role section immediately (server payload corrects it on
      // the next refetch): the picked slot, else the actor's own role.
      ...(showSlotPicker && selectedSlot
        ? { role: { id: selectedSlot.roleId, name: selectedSlot.roleName } }
        : user?.role?.id
          ? { role: { id: user.role.id, name: user.role.name } }
          : {}),
      note: note
        ? {
            note,
            createdById: user.id,
            createdByName: user.name,
            createdAt: new Date().toISOString(),
          }
        : undefined,
      createdBy: {
        id: user.id,
        name: user.name,
      },
      createdAt: new Date().toISOString(),
      status: "Pending",
      completedBy: {},
    };
    const writeOptions = draftToApply
      ? {
          onSuccess: () => applyPendingStageDraft(draftToApply),
          onError: () => {
            // The follow-up itself failed; the PATCH's own handler has already
            // said so. Leaving the draft pending keeps the Stage section
            // showing the pick, so Update is still one click away.
          },
        }
      : undefined;
    // Write to the GRAIN the picked slot lives on: a LEAD-assigned role
    // (the Counsellor) picked while a deal is open must land on the LEAD —
    // a deal-grain row for her role would be holder-less and invisible.
    // `writtenChildId` tracks which DEAL (if any) the write targeted, so the
    // next-due-deal check below can exclude it.
    let writtenChildId: number | null = null;
    const writtenDealId = () =>
      Number.isFinite(Number(currentChildId)) ? Number(currentChildId) : null;
    // DEAL-WISE RBAC (2026-09-13): a lead-assigned role's follow-up lives on
    // the LEAD only while the lead's OWN stage takes that role's follow-ups.
    // When it doesn't (lead 321668: lead in Visa, deal in Qualify), the write
    // goes to the deal being worked — the server keeps it there and judges it
    // on the deal's stage. Unknown participation (map still loading) → the
    // lead, as before.
    const leadStageIdNum = Number(
      (lead as any)?.stage?.id ?? (lead as any)?.stageId,
    );
    const leadStageTakes = (
      roleId: number | null | undefined,
      stageIds: number[] | null | undefined,
    ) => {
      if (roleId == null || !Number.isFinite(leadStageIdNum)) return true;
      if (!Array.isArray(stageIds)) return true;
      return stageIds.map(Number).includes(leadStageIdNum);
    };
    if (showSlotPicker && selectedSlot) {
      if (
        selectedSlot.grain === "lead" &&
        isDealContext &&
        leadStageTakes(
          selectedSlot.roleId,
          followUpRoleStages?.[String(selectedSlot.roleId)],
        )
      ) {
        leadAddFollowUp(followUpObj, writeOptions);
      } else if (isDealContext) {
        childAddFollowUp(followUpObj, writeOptions);
        writtenChildId = writtenDealId();
      } else {
        leadAddFollowUp(followUpObj, writeOptions);
      }
    } else if (isDealContext) {
      // PERSONAL user, deal open: their slot lives where THEY are assigned.
      // A lead-assigned Counsellor rescheduling from a deal-stage drawer
      // must hit the LEAD — writing the deal left her lead-grain overdue row
      // Pending and the lead never left Overdue (32 Skymark leads,
      // 2026-08-31). The server redirects too; this keeps the drawer's
      // local state right without a refetch — unless the lead's OWN stage
      // doesn't take her follow-ups (deal-wise RBAC above): then her slot
      // for this deal is the deal itself.
      const onDeal = dealRows.some(
        (r: any) => r?.user?.id === user?.id && !r?.isRevoked,
      );
      const onLead = leadRows.some(
        (r: any) => r?.user?.id === user?.id && !r?.isRevoked,
      );
      if (
        !onDeal &&
        onLead &&
        leadStageTakes(user?.role?.id, followUpOwnStageIds)
      ) {
        leadAddFollowUp(followUpObj, writeOptions);
      } else {
        childAddFollowUp(followUpObj, writeOptions);
        writtenChildId = writtenDealId();
      }
    } else {
      addFollowUp(followUpObj, writeOptions);
      if (isChildActive) writtenChildId = writtenDealId();
    }
    maybeAdvanceToNextDueDeal(date, writtenChildId);
    // The due popup is NOT scheduled here. The server's per-minute sweep rings
    // the follow-up's assignees over `followup:due` when its date+time passes —
    // which works across devices, reloads, and whoever actually owns the lead.
    // See followUpNotificationSlice.
  };

  const nextDeadline = useMemo(() => {
    if (!Array.isArray(deadlines) || deadlines.length === 0) return null;
    const pending = deadlines.filter((d: any) => d?.status === "Pending");
    if (!pending.length) return null;
    return [...pending].sort((a: any, b: any) => {
      const aw = Number(a?.warningDays) || 0;
      const bw = Number(b?.warningDays) || 0;
      const ae =
        (calendarDaysSince(a?.triggeredAt) ?? 0) - aw;
      const be =
        (calendarDaysSince(b?.triggeredAt) ?? 0) - bw;
      return be - ae;
    })[0];
  }, [deadlines]);

  // Segmented "Deadline" pill that visually mirrors the follow-up chip but
  // dials up contrast so it reads as the priority element on the row:
  // - Solid tinted green body (no /opacity gymnastics on CSS vars)
  // - Saturated counter badge on the right; pulses when past nudge
  // - Amber palette swap when overdue/past-nudge
  // - When no nudge is set we still show a "set" affordance so the right
  //   side never collapses and the pill stays visually balanced.
  //
  // RBAC visibility model — match super-admin parity so view-only roles
  // still SEE that deadlines are a thing on this lead:
  //   • deny  → hide the slot entirely (truly no awareness).
  //   • view  → render the same pills/placeholders the super admin sees,
  //             but inert (no popover, tooltip explains the gate).
  //   • edit  → fully interactive (popover + click affordance).
  // `DeadlineSetterPopover` internally unwraps its trigger when the user
  // can't set, so wrapping it is safe — but for the empty-state we still
  // need a visible placeholder rather than `hidden`, otherwise view-only
  // users get no signal that deadlines exist on this row.
  const deadlineSetter = !canViewDeadline ? null : (
    <DeadlineSetterPopover>
      {nextDeadline ? (
        (() => {
          const target =
            nextDeadline?.toStatus?.name || nextDeadline?.toStatus?.statusName;
          const wd =
            nextDeadline?.warningDays != null
              ? Number(nextDeadline.warningDays)
              : null;
          const elapsed = calendarDaysSince(nextDeadline?.triggeredAt);
          const armed = nextDeadline?.triggeredAt != null && elapsed != null;
          const pastNudge =
            wd != null && elapsed != null && elapsed >= wd;

          const palette = pastNudge
            ? {
                bg: "bg-[#fef3c7]",
                border: "border-[#f59e0b]",
                hover: "hover:bg-[#fde68a]",
                iconBg: "bg-[#f59e0b]",
                iconRing: "ring-[#fcd34d]",
                label: "text-[#92400e]",
                value: "text-[#78350f]",
                counterBg: "bg-[#b45309]",
                counterRing: "ring-[#b45309]/30",
              }
            : {
                bg: "bg-[#ecf5f0]",
                border: "border-[#7fbb9f]",
                hover: "hover:bg-[#dcecde]",
                iconBg: "bg-[#156548]",
                iconRing: "ring-[#7fbb9f]",
                label: "text-[#156548]",
                value: "text-[#0a3a28]",
                counterBg: "bg-[#156548]",
                counterRing: "ring-[#7fbb9f]",
              };

          return (
            <button
              type="button"
              className={cn(
                "group/dl inline-flex items-center gap-2 h-9 pl-1 pr-1 rounded-[10px] cursor-pointer transition-all min-w-0 max-w-[300px] border-[1.5px] shadow-[0_1px_2px_rgba(21,101,72,0.08)]",
                palette.bg,
                palette.border,
                palette.hover
              )}
              title={
                target
                  ? `Milestone target: ${target}${
                      wd != null
                        ? ` · ${armed ? `day ${elapsed} of ${wd}` : `${wd}d nudge`}`
                        : ""
                    }`
                  : "Open milestone setter"
              }
            >
              <span
                className={cn(
                  "shrink-0 inline-flex items-center justify-center size-7 rounded-[7px] ring-1",
                  palette.iconBg,
                  palette.iconRing
                )}
              >
                <Flag
                  className="size-3.5 text-white"
                  strokeWidth={2.75}
                  fill="currentColor"
                />
              </span>
              <div className="flex flex-col items-start leading-[1.05] min-w-0 pr-1">
                <span
                  className={cn(
                    "text-[9.5px] font-bold uppercase tracking-[0.06em]",
                    palette.label
                  )}
                >
                  Deadline
                </span>
                <span
                  className={cn(
                    "text-[12.5px] font-semibold tracking-[-0.2px] truncate max-w-[180px]",
                    palette.value
                  )}
                >
                  {target ?? "Milestone"}
                </span>
              </div>
              <span
                className={cn(
                  "shrink-0 inline-flex items-center justify-center px-2 h-[26px] min-w-[42px] rounded-[7px] text-[11px] font-bold text-white tabular-nums tracking-[-0.2px] ring-1",
                  palette.counterBg,
                  palette.counterRing,
                  pastNudge && "animate-pulse"
                )}
              >
                {wd != null
                  ? armed
                    ? `${elapsed}/${wd}d`
                    : `${wd}d`
                  : "set"}
              </span>
            </button>
          );
        })()
      ) : canSetDeadline ? (
        <button
          type="button"
          className="group/dl inline-flex items-center gap-1.5 h-9 px-3 rounded-[10px] cursor-pointer transition-all border border-dashed border-[#7fbb9f] bg-[#f6fbf8] hover:bg-[#ecf5f0] hover:border-[#156548]"
          title="Set a milestone"
        >
          <Flag
            className="size-3.5 text-[#156548]"
            strokeWidth={2.5}
          />
          <span className="text-[12.5px] font-semibold text-[#156548] tracking-[-0.2px]">
            Set milestone
          </span>
        </button>
      ) : (
        // View-only and no pending milestone — show the SAME placeholder a
        // super admin would see, but inert + tooltip-explained. We render
        // it as a button so the underlying popover can still wrap it (and
        // the popover itself bails when `!canSetDeadline`); the tooltip
        // is what the user actually gets on hover.
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex">
              <button
                type="button"
                disabled
                className="group/dl inline-flex items-center gap-1.5 h-9 px-3 rounded-[10px] transition-all border border-dashed border-[#bcd3c5] bg-[#f6fbf8] cursor-not-allowed opacity-80"
                title="No milestones yet"
              >
                <Flag
                  className="size-3.5 text-[#7fbb9f]"
                  strokeWidth={2.5}
                />
                <span className="text-[12.5px] font-semibold text-[#7fbb9f] tracking-[-0.2px]">
                  No milestone
                </span>
              </button>
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[260px] text-[11px]">
            You can view milestones on this stage but cannot set them.
          </TooltipContent>
        </Tooltip>
      )}
    </DeadlineSetterPopover>
  );

  return (
    <div className="relative w-full">
      {isDealContext ? (
        <input type="hidden" {...childRegister("followUpDate")} />
      ) : (
        <input type="hidden" {...leadRegister("followUpDate")} />
      )}

      {/* "One more deal to follow up here" — announce, animate, glide. The
          progress bar runs the same 2.8s clock as the auto-switch effect;
          "Stay" cancels it. Personal-scope flow only (see
          maybeAdvanceToNextDueDeal). */}
      {nextDueDeal && (
        <div className="absolute bottom-full left-0 right-0 z-30 mb-2 animate-[fuAdvanceIn_.3s_ease-out]">
          <style>{`
            @keyframes fuAdvanceIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
            @keyframes fuAdvanceBar { from { width: 100%; } to { width: 0%; } }
          `}</style>
          <div className="relative overflow-hidden rounded-[12px] border-[1.5px] border-[#7fbb9f] bg-[#ecf5f0] px-3.5 py-2.5 shadow-[0_8px_24px_rgba(21,101,72,0.18)]">
            <div className="flex items-center gap-2.5">
              <span className="inline-flex size-7 shrink-0 items-center justify-center rounded-full bg-[#156548]">
                <Calendar className="size-3.5 text-white" strokeWidth={2.5} />
              </span>
              <div className="min-w-0 flex-1 leading-tight">
                <p className="text-[12.5px] font-semibold text-[#0a3a28]">
                  Follow-up saved — {nextDueDeal.remaining} more deal
                  {nextDueDeal.remaining > 1 ? "s" : ""} to follow up here
                </p>
                <p className="truncate text-[11.5px] text-[#156548]">
                  Moving to{" "}
                  <span className="font-semibold">{nextDueDeal.childName}</span>
                  {nextDueDeal.overdue ? " (overdue)" : ""}…
                </p>
              </div>
              <button
                type="button"
                onClick={() => setNextDueDeal(null)}
                className="shrink-0 rounded-[8px] border border-[#7fbb9f] bg-white px-2.5 py-1 text-[11.5px] font-semibold text-[#156548] transition-colors hover:bg-[#f6fbf8] cursor-pointer"
              >
                Stay
              </button>
            </div>
            <span className="absolute bottom-0 left-0 h-[3px] rounded-r-full bg-[#156548]/70 animate-[fuAdvanceBar_2.8s_linear_forwards]" />
          </div>
        </div>
      )}

      {/* There is no "complete" state any more — `parsedDate` always resolves
          (pending row → form slot → today), so this cluster always renders. */}
      <div className="flex w-full flex-wrap items-center justify-between gap-x-3 gap-y-2">
      {/* Two-pill cluster: Follow-up + Deadline. Both share h-9 / r-10 so
            they read as a unit; wrap on very narrow drawers so actions stay tappable. */}
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
          {/* No chip at all on a rejected entity — not disabled, GONE. */}
          {canViewFollowup && !activeEntityRejected ? (() => {
            const followupChip = (
              <button
                type="button"
                disabled={!canEditFollowup}
                className="group/fu inline-flex items-center gap-2 h-9 pl-1 pr-2.5 rounded-[10px] cursor-pointer transition-all bg-white border-[1.5px] border-[var(--color-black-2)] hover:border-[var(--color-black-4)] hover:bg-gray-50 shrink-0 shadow-[0_1px_2px_rgba(0,0,0,0.04)] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-white disabled:hover:border-[var(--color-black-2)]"
              >
                <span className="shrink-0 inline-flex items-center justify-center size-7 rounded-[7px] bg-[var(--color-black-2)]/60 ring-1 ring-[var(--color-black-2)]">
                  <Calendar
                    className="size-3.5 text-[var(--color-black-8)]"
                    strokeWidth={2.25}
                  />
                </span>
                <div className="flex flex-col items-start leading-[1.05]">
                  <span className="text-[9.5px] font-bold uppercase tracking-[0.06em] text-[var(--color-text-gray)]">
                    Follow-up
                  </span>
                  <span className="text-[12.5px] font-semibold tracking-[-0.2px] tabular-nums text-[var(--color-text-primary)]">
                    {day}
                    <span className="font-normal text-[var(--color-black-6)] mx-0.5">
                      /
                    </span>
                    {month}
                    <span className="font-normal text-[var(--color-black-6)] mx-0.5">
                      /
                    </span>
                    {year}
                  </span>
                </div>
              </button>
            );
            if (!canEditFollowup) {
              return (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex">{followupChip}</span>
                  </TooltipTrigger>
                  <TooltipContent
                    side="top"
                    className="max-w-[260px] text-[11px]"
                  >
                    You can view this follow-up but cannot reschedule it on this stage.
                  </TooltipContent>
                </Tooltip>
              );
            }
            return (
              <DatePickerCalendar
                allowClear={false}
                enableTime={followUpTimeEnabled}
                value={pickerValue}
                onChange={handleDateChange}
                onCreateFollowUp={handleCreateFollowUp}
                scheduleLabels={followUpScheduleLabels}
                slotPicker={slotPickerNode ?? slotBlockedNode}
                slotPickerPending={
                  (showSlotPicker && !selectedSlot) || slotBlockedReason != null
                }
                slotPickerPendingReason={slotBlockedReason ?? undefined}
                maxDate={followUpMaxDay}
                maxDateHint={followUpMaxHint}
              >
                {followupChip}
              </DatePickerCalendar>
            );
          })() : null}

          {dealFollowUpMute && canViewFollowup && !activeEntityRejected && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex h-9 shrink-0 cursor-default items-center gap-1.5 rounded-[10px] border-[1.5px] border-[#C7DDFF] bg-[#EAF3FF] px-2.5 text-[11.5px] font-semibold text-[#1D4ED8]">
                  <Layers className="size-3.5" strokeWidth={2.25} />
                  Muted
                </span>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[280px] text-[11px]">
                This deal's follow-up doesn't count anywhere — the lead's
                newest deal, {describeMutingDeal(dealFollowUpMute)}, is the
                one being followed up (this stage prioritises the latest
                deal).
              </TooltipContent>
            </Tooltip>
          )}

          {deadlineSetter}
        </div>

        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 sm:gap-3">
          {/* Razorpay connected → one button to send a payment link (self-hides otherwise). */}
          <CollectPaymentButton />

          <Button
            type="button"
            variant="outline"
            size="icon"
            className="size-10 shrink-0 rounded-[12px] border-transparent cursor-pointer"
            onClick={() => {
              const href = buildTelHref(
                activeData?.phone,
                (activeData as any)?.phoneCountryCode,
              );
              if (href) {
                window.open(href, "_self");
              } else {
                toast.error("Phone number not available");
              }
            }}
          >
            <Phone className="size-5 text-[var(--color-primary)]" />
          </Button>

          <Button
            type="button"
            variant="outline"
            size="icon"
            className="size-10 shrink-0 rounded-[12px] border-transparent cursor-pointer"
            onClick={() => {
              const url = buildWhatsAppUrl(
                activeData?.phone,
                (activeData as any)?.phoneCountryCode,
              );
              if (url) {
                window.open(url, "_blank");
              } else {
                toast.error("Phone number not available");
              }
            }}
          >
            <MessageCircle className="size-5 text-[var(--color-primary)]" />
          </Button>

          {/*
            Quick Send: opens the Gmail compose modal with the lead's
            email pre-populated and the leadId attached so the server
            can resolve {{lead.*}} merge variables. Falls through to a
            tooltip when the user has no Gmail connected or the lead has
            no email on file — same gating as Phone / WhatsApp icons. */}
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  disabled={!canQuickSend}
                  className="size-10 shrink-0 rounded-[12px] border-transparent cursor-pointer disabled:opacity-50"
                  onClick={() => {
                    if (!leadEmail) {
                      toast.error("Lead has no email on file");
                      return;
                    }
                    if (!gmailAccount) {
                      toast.error(
                        "Your workspace hasn't connected Gmail yet — an admin can connect it on the Gmail page"
                      );
                      return;
                    }
                    dispatch(
                      openCompose({
                        leadId: Number(activeData?.id),
                        prefill: { to: [leadEmail] },
                      })
                    );
                  }}
                >
                  <Mail className="size-5 text-[var(--color-primary)]" />
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent
              side="top"
              className="max-w-[260px] text-[11px]"
            >
              {!gmailAccount
                ? "Your workspace hasn't connected Gmail yet — an admin can connect it on the Gmail page."
                : !leadEmail
                  ? "Lead has no email on file."
                  : "Send a templated email to this lead."}
            </TooltipContent>
          </Tooltip>

          {/* The deal-review CTA is a different action and is NOT covered by
              the Move To Next Stage provision. */}
          {isDealReviewRoute ? (
            <Button
              type="button"
              variant="primary"
              className="h-10 rounded-[12px] px-4 text-[14px] font-medium tracking-[-0.24px] gap-1.5"
              disabled={activePendingDealCount === 0}
              onClick={() => {
                if (isVideoServiceRoute) dispatch(setTriggerReview(true));
                if (isAcademyProgramRoute)
                  dispatch(setAcademyTriggerReview(true));
              }}
            >
              <Sparkles className="size-4" />
              {activePendingDealCount > 0
                ? `Review ${activePendingDealCount} Deal${activePendingDealCount > 1 ? "s" : ""}`
                : "Select Variants"}
              <ArrowRight className="size-5" />
            </Button>
          ) : !showMoveToNextStage ? null : (
            (() => {
              const moveDisabled =
                !nextStage ||
                updateStage.isPending ||
                !canMoveFromCurrentStage;
              const moveButton = (
                <Button
                  type="button"
                  variant="primary"
                  className="h-10 rounded-[12px] px-4 text-[14px] font-medium tracking-[-0.24px]"
                  disabled={moveDisabled}
                  onClick={handleOpenMoveConfirm}
                >
                  {updateStage.isPending
                    ? "Moving..."
                    : nextStage
                      ? `Move To Next Stage`
                      : "Last Stage"}
                  <ArrowRight className="size-5" />
                </Button>
              );

              if (!canMoveFromCurrentStage) return null;

              return moveButton;
            })()
          )}
        </div>
      </div>

      <ConfirmStageMoveDialog
        open={isMoveConfirmOpen}
        onOpenChange={setIsMoveConfirmOpen}
        currentStage={
          currentStage
            ? { id: currentStage.id, name: currentStage.name }
            : null
        }
        targetStage={
          nextStage ? { id: nextStage.id, name: nextStage.name } : null
        }
        targetStatus={
          firstStatusOnNextStage
            ? {
                id: firstStatusOnNextStage.id,
                name: firstStatusOnNextStage.name,
              }
            : undefined
        }
        targetSubStatus={
          firstSubStatusOnNextStage
            ? {
                id: firstSubStatusOnNextStage.id,
                name: firstSubStatusOnNextStage.name,
              }
            : undefined
        }
        mode="next"
        isPending={updateStage.isPending}
        onConfirm={handleConfirmMoveToNextStage}
        confirmLabel={
          nextStage ? `Move to ${nextStage.name}` : "Move lead"
        }
      />
    </div>
  );
}
